/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Reservation } from 'types/entities/Reservation'
import { ReservationDriver } from 'types/entities/ReservationDriver'
import { useAppDispatch } from 'helpers/store'
import BookingLoginAction from 'store/bookingLogin/BookingLoginAction'
import { toast } from 'utils/toastUtils'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiConfirmDialog from 'components/Ui/UiConfirmDialog'
import {
    UiTable,
    UiTableBody,
    UiTableCell,
    UiTableHead,
    UiTableHeaderCell,
    UiTableRow,
} from 'components/Ui/UiTable'
import UiButton, {
    UiButtonSize,
    UiButtonStyle,
    UiButtonType,
} from 'components/Ui/UiButton'
import {
    PencilSquareIcon,
    TrashIcon,
    UserPlusIcon,
} from '@heroicons/react/24/outline'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    useBookingSellerReservationPersonSchema,
    BookingSellerReservationPersonInputs,
} from '../../schemas/useBookingSellerReservationPersonSchema'
import UiInput from 'components/Ui/UiInput'
import UiDatePicker from 'components/Ui/UiDatePicker'
import UiSelectWithGraphics from 'components/Ui/UiSelectWithGraphics'
import { UserType } from 'types/enums/UserType'
import Dates from 'utils/Dates'
import { DATE_FORMATS } from 'constants/DateFormats'
import { PackageItemItemType } from 'types/enums/PackageItemItemType'
import { ArticleTicketType } from 'types/enums/ArticleTicketType'
import { translate } from 'utils/utility'

interface BookingSellerReservationPersonsModalProps {
    reservation: Reservation
    onSuccess?: () => void
}

type FormData = BookingSellerReservationPersonInputs

const BookingSellerReservationPersonsModal: React.FC<
    BookingSellerReservationPersonsModalProps
> = ({ reservation, onSuccess }) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [currentReservation, setCurrentReservation] = useState(reservation)
    const [editingDriver, setEditingDriver] =
        useState<ReservationDriver | null>(null)
    const [isAdding, setIsAdding] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [driverToDelete, setDriverToDelete] =
        useState<ReservationDriver | null>(null)

    const accessTickets =
        currentReservation.packagesCart?.packagesCartItems?.filter(
            (item) =>
                item.packageItem?.type === PackageItemItemType.PackageItem &&
                item.packageItem.article?.ticket_type ===
                    ArticleTicketType.Access &&
                Number.isInteger(item.id) &&
                item.id > 0
        ) || []

    const getDriverAccessTickets = (driver: ReservationDriver) => {
        const linkedCartItems = accessTickets.filter(
            (ticket) => ticket.reservation_driver_id === driver.id
        )

        if (linkedCartItems.length > 0) return linkedCartItems

        return (driver.packagesCartItems || []).filter(
            (item) =>
                item.packageItem?.type === PackageItemItemType.PackageItem &&
                item.packageItem.article?.ticket_type ===
                    ArticleTicketType.Access
        )
    }

    const assignableAccessTickets = accessTickets.filter(
        (ticket) =>
            ticket.reservation_driver_id == null ||
            ticket.reservation_driver_id === editingDriver?.id
    )

    const ticketOptions = assignableAccessTickets.map((ticket) => ({
        value: ticket.id,
        label: translate(
            (ticket.article_shortname || ticket.packageItem?.name || '') as any
        ),
    }))

    const BookingSellerReservationPersonSchema =
        useBookingSellerReservationPersonSchema()
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<FormData>({
        resolver: zodResolver(BookingSellerReservationPersonSchema),
        defaultValues: {
            name: '',
            last_name: '',
            email: '',
            birthdate: '',
            packages_cart_item_id: assignableAccessTickets[0]?.id || 0,
        },
    })

    const onEditHandler = (driver: ReservationDriver) => {
        const assignedAccessTickets = getDriverAccessTickets(driver)
        setEditingDriver(driver)
        setIsAdding(true)
        reset({
            name: driver.driver?.name || driver.name || '',
            last_name: driver.driver?.last_name || '',
            email: driver.driver?.email || '',
            birthdate: driver.driver?.birthdate || '',
            packages_cart_item_id:
                assignedAccessTickets[0]?.id ||
                assignableAccessTickets[0]?.id ||
                0,
        })
    }

    const onDeleteHandler = (driver: ReservationDriver) => {
        setDriverToDelete(driver)
    }

    const onConfirmDelete = async () => {
        if (!driverToDelete) return
        toast.info(t('Delete person functionality to be implemented in API'))
        setDriverToDelete(null)
    }

    const onSubmit = async (data: FormData) => {
        if (!editingDriver && assignableAccessTickets.length === 0) {
            toast.error(t('No access ticket available for this reservation'))
            return
        }

        setIsLoading(true)
        try {
            const birthdateYMD = Dates.ymd(
                data.birthdate,
                DATE_FORMATS.DATE_YMD
            )

            if (!birthdateYMD) {
                toast.error(t('Invalid birthdate format'))
                return
            }

            const payload = {
                reservation: currentReservation,
                customer: {
                    ...data,
                    name: data.name,
                    last_name: data.last_name,
                    nickname: '',
                    birthdate: birthdateYMD,
                    user_type: UserType.Guest,
                    reservation_driver_id: editingDriver?.id || undefined,
                    packages_cart_item_id: data.packages_cart_item_id,
                },
            }

            if (editingDriver) {
                await dispatch(
                    BookingLoginAction.fetchUpdateCustomerFromReservationNew(
                        payload
                    )
                )
            } else {
                await dispatch(
                    BookingLoginAction.fetchSetCustomerToReservationNew(payload)
                )
            }

            if (editingDriver) {
                toast.success(t('Person updated successfully'))
            } else {
                toast.success(t('Person added successfully'))
            }

            setIsAdding(false)
            setEditingDriver(null)
            reset()
            onSuccess?.()
            dispatch(
                BookingLoginAction.fetchSellerReservationDetail(
                    currentReservation.id
                )
            )

            const response = await dispatch(
                BookingLoginAction.fetchReservationDetail(
                    currentReservation.id
                )
            )
            setCurrentReservation(response.reservation)
        } catch (error) {
            console.error('Error saving person:', error)
            toast.error(t('Failed to save person'))
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <UiFormModalShell
            title={t('Manage Persons')}
            showHeader
            className="BookingSellerReservationPersonsModal max-h-[80vh]"
        >
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-72px)]">
                {!isAdding ? (
                    <div className="flex flex-col gap-4">
                        <div className="flex justify-end">
                            <UiButton
                                title={t('Add Person')}
                                icon={<UserPlusIcon className="w-4 h-4" />}
                                style={UiButtonStyle.Primary}
                                size={UiButtonSize.Small}
                                disabled={assignableAccessTickets.length === 0}
                                onClick={() => {
                                    setIsAdding(true)
                                    setEditingDriver(null)
                                    reset({
                                        name: '',
                                        last_name: '',
                                        email: '',
                                        birthdate: '',
                                        packages_cart_item_id:
                                            assignableAccessTickets[0]?.id || 0,
                                    })
                                }}
                            />
                        </div>
                        {assignableAccessTickets.length === 0 && (
                            <div className="text-xs text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-900/30 rounded-md px-3 py-2">
                                {t('No access ticket available for this reservation')}
                            </div>
                        )}
                        <UiTable>
                            <UiTableHead>
                                <UiTableHeaderCell>
                                    {t('Name')}
                                </UiTableHeaderCell>
                                <UiTableHeaderCell>
                                    {t('Email')}
                                </UiTableHeaderCell>
                                <UiTableHeaderCell>
                                    {t('Ticket')}
                                </UiTableHeaderCell>
                                <UiTableHeaderCell>
                                    {t('Birthdate')}
                                </UiTableHeaderCell>
                                <UiTableHeaderCell align="right">
                                    {t('Actions')}
                                </UiTableHeaderCell>
                            </UiTableHead>
                            <UiTableBody>
                                {currentReservation.reservationDrivers?.map(
                                    (driver) => (
                                        <UiTableRow key={driver.id}>
                                            <UiTableCell className="font-medium text-gray-900 dark:text-gray-100">
                                                {driver.driver
                                                    ? `${driver.driver.name} ${driver.driver.last_name}`
                                                    : driver.name}
                                            </UiTableCell>
                                            <UiTableCell className="text-gray-600 dark:text-gray-400">
                                                {driver.driver?.email || '---'}
                                            </UiTableCell>
                                            <UiTableCell className="text-gray-600 dark:text-gray-400">
                                                {getDriverAccessTickets(driver)[0]
                                                    ? translate(
                                                        (getDriverAccessTickets(
                                                            driver
                                                        )[0]
                                                            .article_shortname ||
                                                            getDriverAccessTickets(
                                                                driver
                                                            )[0]
                                                                .packageItem
                                                                ?.name ||
                                                            '') as any
                                                    )
                                                    : '---'}
                                            </UiTableCell>
                                            <UiTableCell className="text-gray-600 dark:text-gray-400">
                                                {driver.driver?.birthdate ||
                                                    '---'}
                                            </UiTableCell>
                                            <UiTableCell align="right">
                                                <div className="flex justify-end gap-2">
                                                    <UiButton
                                                        title=""
                                                        icon={
                                                            <PencilSquareIcon className="w-4 h-4" />
                                                        }
                                                        style={
                                                            UiButtonStyle.Secondary
                                                        }
                                                        size={
                                                            UiButtonSize.Small
                                                        }
                                                        onClick={() =>
                                                            onEditHandler(
                                                                driver
                                                            )
                                                        }
                                                    />
                                                    <UiButton
                                                        title=""
                                                        icon={
                                                            <TrashIcon className="w-4 h-4" />
                                                        }
                                                        style={
                                                            UiButtonStyle.Red
                                                        }
                                                        size={
                                                            UiButtonSize.Small
                                                        }
                                                        onClick={() =>
                                                            onDeleteHandler(
                                                                driver
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </UiTableCell>
                                        </UiTableRow>
                                    )
                                )}
                                {(!currentReservation.reservationDrivers ||
                                    currentReservation.reservationDrivers.length ===
                                    0) && (
                                        <UiTableRow>
                                            <UiTableCell
                                                colSpan={5}
                                                className="text-center py-8 text-gray-400 italic"
                                            >
                                                {t(
                                                    'No persons assigned to this reservation.'
                                                )}
                                            </UiTableCell>
                                        </UiTableRow>
                                    )}
                            </UiTableBody>
                        </UiTable>
                    </div>
                ) : (
                    <form
                        onSubmit={handleSubmit(onSubmit)}
                        className="flex flex-col gap-4"
                    >
                        <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-gray-100">
                            {editingDriver
                                ? t('Edit Person')
                                : t('Add New Person')}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Controller
                                name="name"
                                control={control}
                                render={({ field }) => (
                                    <UiInput
                                        id="name"
                                        label={t('First Name')}
                                        placeholder={t('Enter first name')}
                                        error={errors.name?.message}
                                        required
                                        {...field}
                                    />
                                )}
                            />
                            <Controller
                                name="last_name"
                                control={control}
                                render={({ field }) => (
                                    <UiInput
                                        id="last_name"
                                        label={t('Last Name')}
                                        placeholder={t('Enter last name')}
                                        error={errors.last_name?.message}
                                        required
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Controller
                                name="email"
                                control={control}
                                render={({ field }) => (
                                    <UiInput
                                        id="email"
                                        label={t('Email')}
                                        placeholder={t('Enter email address')}
                                        error={errors.email?.message}
                                        {...field}
                                    />
                                )}
                            />
                            <Controller
                                name="packages_cart_item_id"
                                control={control}
                                render={({ field }) => (
                                    <UiSelectWithGraphics
                                        label={t('Access Ticket')}
                                        selected={field.value}
                                        setSelected={(val) =>
                                            field.onChange(Number(val))
                                        }
                                        options={ticketOptions}
                                        required
                                        debounceMs={0}
                                    />
                                )}
                            />
                        </div>
                        <UiDatePicker
                            name="birthdate"
                            control={control}
                            label={t('Birthdate')}
                            error={errors.birthdate?.message}
                            required
                        />
                        <div className="flex justify-end gap-3 mt-4">
                            <UiButton
                                title={t('Cancel')}
                                style={UiButtonStyle.Secondary}
                                onClick={() => {
                                    setIsAdding(false)
                                    setEditingDriver(null)
                                }}
                            />
                            <UiButton
                                title={editingDriver ? t('Update') : t('Add')}
                                style={UiButtonStyle.Primary}
                                type={UiButtonType.Submit}
                                disabled={
                                    isLoading ||
                                    (!editingDriver &&
                                        assignableAccessTickets.length === 0)
                                }
                            />
                        </div>
                    </form>
                )}
            </div>
            {driverToDelete && (
                <UiConfirmDialog
                    title={t('Remove Person')}
                    message={t('Are you sure you want to remove this person?')}
                    confirmText={t('Remove')}
                    cancelText={t('Cancel')}
                    onConfirm={onConfirmDelete}
                    onCancel={() => setDriverToDelete(null)}
                    danger
                />
            )}
        </UiFormModalShell>
    )
}

export default BookingSellerReservationPersonsModal
